import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { 
  GraduationCap, Bot, Image, BookOpen, Info, Phone, MapPin, Mail,
  MessageCircle
} from 'lucide-react';

const Footer = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const contactInfo = {
    phones: ['+91 97167 94909', '+91 96255 83120', '+91 7827272198'],
    whatsapp: ['+91 97167 94909', '+91 96255 83120', '+91 7827272198'],
    mapUrl: 'https://maps.app.goo.gl/iFAR95WE5K4G7V4v6?g_st=aw',
  };

  const quickLinks = [
    { path: '/classes', label: 'Classes', icon: GraduationCap },
    { path: '/dauter-ai', label: 'Dauter AI', icon: Bot },
    { path: '/gallery', label: 'Gallery', icon: Image },
    { path: '/blog', label: 'Blog', icon: BookOpen },
    { path: '/about', label: 'About Us', icon: Info },
  ];

  return (
    <footer className="bg-[#0A0A0A] border-t border-zinc-800" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-1 mb-4">
              <span className="logo-sp text-3xl text-white">SP</span>
              <span className="logo-sp text-3xl logo-dot">.</span>
            </Link>
            <p className="text-zinc-500 text-sm">
              {settings?.hero_subtitle || 'Journey Towards Excellence'}
            </p>
            <p className="text-zinc-400 mt-4 text-sm">
              {settings?.footer_content || 'Empowering students from class 6th to 12th with quality education and guidance.'}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-zinc-500 hover:text-white text-sm transition-colors flex items-center gap-2"
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-4 flex items-center gap-2">
              <Phone className="w-4 h-4 text-red-500" />
              Contact Us
            </h4>
            <ul className="space-y-2 text-sm">
              {contactInfo.phones.map((phone, idx) => (
                <li key={idx}>
                  <a
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="text-zinc-500 hover:text-white transition-colors flex items-center gap-2"
                  >
                    <Phone className="w-3 h-3" />
                    {phone}
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <a
                  href={contactInfo.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-500 hover:text-red-400 transition-colors flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4" />
                  View on Maps →
                </a>
              </li>
            </ul>
          </div>

          {/* WhatsApp */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-4 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-green-500" />
              WhatsApp
            </h4>
            <ul className="space-y-2 text-sm">
              {contactInfo.whatsapp.map((phone, idx) => (
                <li key={idx}>
                  <a
                    href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-500 hover:text-green-400 transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    {phone}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-zinc-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-600 text-sm">
            © {new Date().getFullYear()} STUDY POINT. All rights reserved.
          </p>
          <p className="text-zinc-600 text-sm">
            Crafted by{' '}
            <a
              href="https://gakumedia.space/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-500 hover:text-red-400 transition-colors"
            >
              GAKU MEDIA
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
